import { useEditor, EditorContent } from "@tiptap/react";
import { useEffect } from "react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Icon } from "@ann/web-components";
import { RichTextOptionsContainer, RichTextContainer } from "./RichTextEditor.styled";
import { TextAlign } from '@tiptap/extension-text-align';

const RichTextEditor = ({ content = "", onChange, disabled = false }) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Link,
            TextAlign.configure({
                types: ['paragraph', 'heading'],
                alignments: ['left', 'center', 'right', 'justify'],
                defaultAlignment: null
            })
        ],
        content: content,
        editable: !disabled,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const text = editor.getText();
            
            if (onChange) {
                onChange(html || text);
            }
        }
    });

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [disabled, editor]);

    if (!editor) return null;

    return (
        <RichTextContainer>
            <RichTextOptionsContainer className="toolbar">
                <Icon 
                    name="bold" 
                    onClick={() => !disabled && editor.chain().focus().toggleBold().run()} 
                    className={`IconOption ${editor.isActive("bold") ? "active" : ""} ${disabled ? "disabled" : ""}`} 
                />
                <Icon 
                    name="italic" 
                    onClick={() => !disabled && editor.chain().focus().toggleItalic().run()} 
                    className={`IconOption ${editor.isActive("italic") ? "active" : ""} ${disabled ? "disabled" : ""}`} 
                />
                <Icon 
                    name="strikethrough" 
                    onClick={() => !disabled && editor.chain().focus().toggleStrike().run()} 
                    className={`IconOption ${editor.isActive("strike") ? "active" : ""} ${disabled ? "disabled" : ""}`} 
                />
                <Icon 
                    name="numberedList" 
                    onClick={() => !disabled && editor.chain().focus().toggleOrderedList().run()} 
                    className={`IconOption ${editor.isActive("orderedList") ? "active" : ""} ${disabled ? "disabled" : ""}`} 
                />
                <Icon 
                    name="bulletList" 
                    onClick={() => !disabled && editor.chain().focus().toggleBulletList().run()} 
                    className={`IconOption ${editor.isActive("bulletList") ? "active" : ""} ${disabled ? "disabled" : ""}`} 
                />
                <Icon
                    name="alignLeft"
                    onClick={() => !disabled && editor.chain().focus().setTextAlign('left').run()}
                    className={`IconOption ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''} ${disabled ? "disabled" : ""}`}
                />
                <Icon
                    name="alignCenter"
                    onClick={() => !disabled && editor.chain().focus().setTextAlign('center').run()}
                    className={`IconOption ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''} ${disabled ? "disabled" : ""}`}
                />
                <Icon
                    name="alignRight"
                    onClick={() => !disabled && editor.chain().focus().setTextAlign('right').run()}
                    className={`IconOption ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''} ${disabled ? "disabled" : ""}`}
                />
                <Icon 
                    name="link" 
                    onClick={() => {
                        if (disabled) return;
                        const url = prompt("Enter URL");
                        if (url) editor.chain().focus().setLink({ href: url }).run();
                    }} 
                    className={`IconOption ${disabled ? "disabled" : ""}`} 
                />
            </RichTextOptionsContainer>

            <EditorContent editor={editor} className="ProseMirror" />
        </RichTextContainer>
    );
};

export default RichTextEditor;
